import {
  Blurb,
  Prize,
  Sponsor,
} from 'functions/src/types/firestore/Game/Competition';
import {
  HeatEndpoint,
  QualificationConditions,
  RegistrationOption,
  TournamentDetail,
} from 'functions/src/types/firestore/Game/Tournament';
import { ValueWrapper } from 'src/components/data-entry-ui/ValueWrapper';
import { createArrayField } from 'src/util/data-entry-ui/create-form-inputs/createArrayField';
import { createObjectField } from 'src/util/data-entry-ui/create-form-inputs/createObjectField';
import { createPrimitiveField } from 'src/util/data-entry-ui/create-form-inputs/createPrimitiveField';

export const getBlurb = (blurbInput: Blurb) => {
  const blurbFields = [
    {
      fieldName: 'title',
      regex: /[\s\S]*/,
      regexExplained: 'Please enter a valid title.',
      value: blurbInput.title,
    },
    {
      fieldName: 'description',
      value: blurbInput.description,
      regex: /[\s\S]*/,
      regexExplained: 'Please enter a valid description',
    },
  ];
  const blurb = createObjectField<Blurb>(
    blurbFields.map(createPrimitiveField),
    'Blurb',
    'blurb',
  );
  return blurb;
};

export const getPrize = (prizeInput: Prize) => {
  const prizeFields = [
    {
      fieldName: 'amount',
      value: prizeInput.amount || 0,
      label: 'Prize Amount',
      required: true,
      regexExplained:
        'Please enter a valid prize amount. It must be a positive integer.',
      regex: /^(0|[1-9]\d*)$/,
    },
    {
      fieldName: 'currency',
      value: prizeInput.currency || 'USD',
      label: 'Prize Currency',
      required: true,
      regexExplained:
        'Please enter a valid prize currency with a maximum of 20 characters.',
      regex: /^.{1,20}$/,
    },
  ];
  const prize = createObjectField<Prize>(
    prizeFields.map(createPrimitiveField),
    'Prize',
    'prize',
  );
  return prize;
};

export const getEndpoints = (endpointsInput?: HeatEndpoint[]) => {
  const endpointsFormatted: ValueWrapper<HeatEndpoint>[] =
    endpointsInput?.map((endpoint) => {
      return {
        fields: {
          url: {
            value: endpoint.url || '',
            label: 'Endpoint Url',
            required: true,
            regex: /^https:\/\/.+$/,
            regexExplained: 'Enter a valid url starting with https://',
          },
          pollLevel: {
            value: endpoint.pollLevel || '',
            label: 'Poll Level',
            regex: /^(heat|result)$/,
            regexExplained:
              'Enter a valid poll level. It must be either "heat" or "result".',
          },
          method: {
            value: endpoint.method || '',
            label: 'Method',
            regex: /^(GET|POST)$/,
            regexExplained:
              'Enter a valid method. It must be either "GET" or "POST".',
          },
          postBody: {
            value: false,
            label: 'Post Body',
          },
        },
        title: 'Endpoint Url',
      };
    }) || [];
  const endpointFields = [
    {
      fieldName: 'url',
      value: '',
      required: true,
      regex: /^https:\/\/.+$/,
      regexExplained: 'Enter a valid url starting with https://',
    },
    {
      fieldName: 'pollLevel',
      value: '',
      regex: /^(heat|result)$/,
      regexExplained:
        'Enter a valid poll level. It must be either "heat" or "result".',
    },
    {
      fieldName: 'method',
      value: '',
      regex: /^(GET|POST)$/,
      regexExplained:
        'Enter a valid method. It must be either "GET" or "POST".',
    },
    {
      value: false,
      fieldName: 'postBody',
    },
  ].map(createPrimitiveField);
  const endpoints = createArrayField<HeatEndpoint>(
    'endpoints',
    [createObjectField(endpointFields, 'Endpoint Url')],
    'Endpoints',
    endpointsFormatted,
  );
  return endpoints;
};

export const getPayouts = (payoutsInput?: Prize[]) => {
  const payoutsFormatted: ValueWrapper<Prize>[] =
    payoutsInput?.map((payout) => {
      return {
        fields: {
          amount: {
            value: payout.amount || 0,
            label: 'Amount',
            regexExplained:
              'Please enter a valid prize amount. It must be a positive integer.',
            regex: /^(0|[1-9]\d*)$/,
          },
          currency: {
            value: 'USD',
            label: 'Currency',
            regexExplained:
              'Please enter a valid payout currency with a maximum of 20 characters.',
            regex: /^.{1,20}$/,
          },
        },
        title: 'Payout',
      };
    }) || [];
  const payoutsFields = [
    {
      fieldName: 'amount',
      value: 0,
      regex: /^(0|[1-9]\d*)$/,
      regexExplained:
        'Please enter a valid prize amount. It must be a positive integer.',
    },
    {
      fieldName: 'currency',
      value: 'USD',
      regex: /^.{1,20}$/,
      regexExplained:
        'Please enter a valid payout currency with a maximum of 20 characters.',
    },
  ].map(createPrimitiveField);
  const payouts = createArrayField<Prize>(
    'payout',
    [createObjectField(payoutsFields, 'Payout')],
    'Payouts',
    payoutsFormatted,
  );
  return payouts;
};

export const getRegistrationOptions = (
  registrationOptionsInput: RegistrationOption[],
) => {
  const registrationOptionsFormatted: ValueWrapper<RegistrationOption>[] =
    registrationOptionsInput?.map((option) => {
      return {
        fields: {
          id: {
            value: option.id || '',
            label: 'ID',
            regex: /^[a-z][a-zA-Z]*$/,
            regexExplained:
              'Please enter a valid ID in camel case (e.g. emailAddress, ethWallet).',
          },
          name: {
            value: option.name || '',
            label: 'Name',
            // eslint-disable-next-line security/detect-unsafe-regex
            regex: /^[A-Z][a-zA-Z]*(?:\s[A-Z][a-zA-Z]*)*$/,
            regexExplained:
              'Please enter a valid name in title case (e.g. Email Address, Eth Wallet).',
          },
          preamble: {
            value: option.preamble || '',
            label: 'Preamble',
            regex: /^.{1,100}$/,
            regexExplained:
              'Please enter a valid preamble with a maximum of 100 characters.',
          },
          postambleLink: {
            value: option.postambleLink || '',
            label: 'Postamble Link',
            regex: /^(https:\/\/.+|)$/,
            regexExplained:
              'Please enter a valid URL that starts with "https://".',
          },
          postambleText: {
            value: option.postambleText || '',
            label: 'Postamble Text',
            regex: /^.{0,100}$/,
            regexExplained:
              'Please enter a valid postamble text with a maximum of 100 characters.',
          },
          formatExample: {
            value: option.formatExample || '',
            label: 'Format Example',
            regex: /^.*$/,
            regexExplained:
              'Please enter a valid format example. Any characters are allowed.',
          },
          regex: {
            value: option.regex || '',
            label: 'Regex',
            regex: /^\/((?:\\\/|[^/])+)\/([gimuy]*)$/,
            regexExplained:
              'Please enter a valid regex expression in the format /pattern/flags (e.g. /\\d+/g).',
          },
          optional: {
            value: option.optional,
            label: 'Optional',
          },
        },
        title: 'Registration Option',
      };
    }) || [];
  // eslint-disable-next-line @blumintinc/blumint/extract-global-constants
  const registrationOptionsFields = [
    {
      fieldName: 'id',
      value: '',
      regex: /^[a-z][a-zA-Z]*$/,
      regexExplained:
        'Please enter a valid ID in camel case (e.g. emailAddress, ethWallet).',
    },
    {
      fieldName: 'name',
      value: '',
      // eslint-disable-next-line security/detect-unsafe-regex
      regex: /^[A-Z][a-zA-Z]*(?:\s[A-Z][a-zA-Z]*)*$/,
      regexExplained:
        'Please enter a valid name in title case (e.g. Email Address, Eth Wallet).',
    },
    {
      fieldName: 'preamble',
      value: '',
      regex: /^.{1,100}$/,
      regexExplained:
        'Please enter a valid preamble with a maximum of 100 characters.',
    },
    {
      fieldName: 'postambleLink',
      value: '',
      regex: /^https:\/\/.+$/,
      regexExplained: 'Please enter a valid URL that starts with "https://".',
    },
    {
      fieldName: 'postambleText',
      value: '',
      regex: /^.{1,100}$/,
      regexExplained:
        'Please enter a valid postamble text with a maximum of 100 characters.',
    },
    {
      fieldName: 'formatExample',
      value: '',
      regex: /^.*$/,
      regexExplained:
        'Please enter a valid format example. Any characters are allowed.',
    },
    {
      fieldName: 'regex',
      value: '[sS]*',
      regex: /^\/((?:\\\/|[^/])+)\/([gimuy]*)$/,
      regexExplained:
        'Please enter a valid regex expression in the format /pattern/flags (e.g. /\\d+/g).',
    },
    {
      fieldName: 'optional',
      value: false,
    },
  ].map(createPrimitiveField);
  const registrationOptions = createArrayField<RegistrationOption>(
    'registrationOptions',
    [createObjectField(registrationOptionsFields, 'Registration Option')],
    'Registration Options',
    registrationOptionsFormatted,
  );
  return registrationOptions;
};

export const getTournamentDetails = (
  tournamentDetailsInput: TournamentDetail[],
) => {
  const tournamentDetailsFormatted: ValueWrapper<TournamentDetail>[] =
    tournamentDetailsInput?.map((detail) => {
      return {
        fields: {
          title: {
            value: detail.title || '',
            label: 'Title',
            regexExplained:
              'Please enter a valid heading for the tournament detail accordion. Each word should start with a capital letter if there are two or more words. E.g. Rules And Regulations, How To Play, etc.',
            // eslint-disable-next-line security/detect-unsafe-regex
            regex: /^([A-Z][a-z\d]*)(\s[A-Z][a-z\d]*)*$/,
            required: true,
          },
          body: {
            value: detail.body || '',
            label: 'Body',
            regexExplained:
              'Enter the details of what your title above represents. E.g. if your title is "Rules and Regulations", you should write your tournament rules and regulations in detail here',
            regex: /[\s\S]*/,
            multiline: true,
            required: true,
          },
        },
        title: 'Detail ',
      };
    }) || [];

  const tournamentDetailsFields = [
    {
      fieldName: 'title',
      value: '',
      regexExplained:
        'Please enter a valid heading for the tournament detail accordion. Each word should start with a capital letter if there are two or more words. E.g. Rules And Regulations, How To Play, etc.',
      // eslint-disable-next-line security/detect-unsafe-regex
      regex: /^([A-Z][a-z\d]*)(\s[A-Z][a-z\d]*)*$/,
      required: true,
    },
    {
      fieldName: 'body',
      value: '',
      regexExplained:
        'Enter the details of what your title above represents. E.g. if your title is "Rules and Regulations", you should write your tournament rules and regulations in detail here',
      regex: /[\s\S]*/,
      multiline: true,
      required: true,
    },
  ].map(createPrimitiveField);

  const tournamentDetails = createArrayField<TournamentDetail>(
    'tournamentDetails',
    [createObjectField(tournamentDetailsFields, 'Detail')],
    'Tournament Details',
    tournamentDetailsFormatted,
  );
  return tournamentDetails;
};

export const getQualification = (
  qualificationInput?: QualificationConditions[],
) => {
  const qualificationsFormatted: ValueWrapper<QualificationConditions>[] =
    qualificationInput?.map((qualificationDetail) => {
      return {
        fields: {
          type: {
            value: 'proportion',
            label: 'Type',
            regex: /^(proportion|top)$/,
            regexExplained: 'Please enter either "proportion" or "top".',
          },
          proportion: {
            value: 0,
            label: 'Proportion',
            // eslint-disable-next-line security/detect-unsafe-regex
            regex: /^(0(\.\d{1,3})?|1(\.0{1,3})?)$/,
            disabled: qualificationDetail?.[0]?.type === 'top',
            regexExplained:
              'Please enter a valid proportion value between 0 and 1 (inclusive).',
          },
          numberOfPlaces: {
            value: 0,
            label: 'Number of Places',
            regex: /^[1-9]\d*$/,
            disabled: qualificationDetail?.[0]?.type === 'proportion',
            regexExplained:
              'Please enter a valid number of places (positive integer).',
          },
        },
        title: 'Qualification',
      };
    }) || [];

  const qualificationFields = [
    {
      fieldName: 'type',
      value: 'proportion',
      regex: /^(proportion|top)$/,
      regexExplained: 'Please enter either "proportion" or "top".',
    },
    {
      fieldName: 'proportion',
      value: 0,
      // eslint-disable-next-line security/detect-unsafe-regex
      regex: /^(0(\.\d{1,3})?|1(\.0{1,3})?)$/,
      disabled: qualificationInput?.[0]?.type === 'top',
      regexExplained:
        'Please enter a valid proportion value between 0 and 1 (inclusive).',
    },
    {
      fieldName: 'numberOfPlaces',
      value: 0,
      regex: /^[1-9]\d*$/,
      disabled: qualificationInput?.[0]?.type === 'proportion',
      regexExplained:
        'Please enter a valid number of places (positive integer).',
    },
  ].map(createPrimitiveField);

  const qualification = createArrayField<QualificationConditions>(
    'qualification',
    [createObjectField(qualificationFields, 'Condition')],
    'Qualification',
    qualificationsFormatted,
  );
  return qualification;
};

export const getSponsors = (sponsorsInput?: Sponsor[]) => {
  const sponsorsFormatted: ValueWrapper<Sponsor>[] =
    sponsorsInput?.map((sponsor) => {
      return {
        fields: {
          name: {
            value: sponsor.name || '',
            label: 'Name',
            regexExplained:
              'Please enter a valid sponsor name, you will be allowed to enter letters, numbers or any special characters the sponsor name might contain.',
            regex: /^.*$/,
          },
          height: {
            value: sponsor.height || 200,
            label: 'Height',
            regexExplained:
              'Please enter a valid height, that is less than 500px',
            regex: /^(0|[1-9]\d{0,5}|500)$/,
          },
          width: {
            value: sponsor.width || 200,
            label: 'Width',

            regexExplained:
              'Please enter a valid width, that is less than 500px',
            regex: /^(0|[1-9]\d{0,5}|500)$/,
          },
          imgUrl: {
            value: sponsor.imgUrl || '',
            label: 'Image URL',
            regexExplained:
              'Please enter a valid image url hosted on Firebase Storage. The URL should start with "https://firebasestorage.googleapis.com/v0/b/blumint-".',
            regex:
              /^https:\/\/firebasestorage\.googleapis\.com\/v0\/b\/blumint-(development|production)\.appspot\.com\/o\/Game%/,
          },
          redirectUrl: {
            value: sponsor.redirectUrl || '',
            label: 'Redirect URL',
            regexExplained: 'Please enter a valid redirect url',
            regex: /^https:\/\/.+$/,
          },
        },
        title: 'Sponsor',
      };
    }) || [];
  const sponsorFields = [
    {
      fieldName: 'name',
      value: '',
      regexExplained:
        'Please enter a valid sponsor name, you will be allowed to enter letters, numbers or any special characters the sponsor name might contain.',
      regex: /^.*$/,
    },
    {
      fieldName: 'height',
      value: 200,
      label: 'Height',
      regexExplained: 'Please enter a valid height, that is less than 500px',
      regex: /^(0|[1-9]\d{0,5}|500)$/,
    },
    {
      fieldName: 'width',
      value: 200,
      regexExplained: 'Please enter a valid width, that is less than 500px',
      regex: /^(0|[1-9]\d{0,5}|500)$/,
    },
    {
      fieldName: 'imgUrl',
      value: '',
      regexExplained:
        'Please enter a valid image url hosted on Firebase Storage. The URL should start with "https://firebasestorage.googleapis.com/v0/b/blumint-".',
      regex:
        /^https:\/\/firebasestorage\.googleapis\.com\/v0\/b\/blumint-(development|production)\.appspot\.com\/o\/Game%/,
    },
    {
      fieldName: 'redirectUrl',
      value: '',
      regexExplained: 'Please enter a valid redirect url',
      regex: /^https:\/\/.+$/,
    },
  ].map(createPrimitiveField);
  const sponsors = createArrayField<Sponsor>(
    'sponsors',
    [createObjectField(sponsorFields, 'sponsor')],
    'Sponsors',
    sponsorsFormatted,
  );
  return sponsors;
};
